import { getProvider } from '@/apollo'
import gql from 'graphql-tag'
import { splitError } from '@/helper/splitError'

const initialState = {
  isMfaLoading: false,
  msisdn: '',
  isMfaActive: false,
  isMfaCompleted: false,
  remainingAttempts: 10,
  token: '',
  method: '',
  mfaTokenValidUntil: null
}

const MfaModule = {
  namespaced: true,
  state: initialState,
  getters: {
    isMfaLoading: state => state.isMfaLoading,
    isMfaActive: state => state.isMfaActive,
    isMfaCompleted: state => state.isMfaCompleted,
    getMsisdn: state => state.msisdn,
    getToken: state => state.token,
    getMethod: state => state.method,
    getRemainingAttempts: state => state.remainingAttempts,
    isMfaTokenValid (state) {
      if (state.mfaTokenValidUntil) {
        const validUntilDate = new Date(state.mfaTokenValidUntil)
        const currentDate = new Date()

        // Compare validUntil with current date
        return validUntilDate > currentDate
      }
      return false
    }
  },
  mutations: {
    resetMfaState (state) {
      Object.assign(state, initialState)
    },
    setMfaLoading (state, loading) {
      state.isMfaLoading = loading
    },
    setMfaStatus (state, status) {
      state.isMfaActive = status
    },
    setIsMfaCompleted (state, isMfaCompleted) {
      state.isMfaCompleted = isMfaCompleted
    },
    setMsisdn (state, msisdn) {
      state.msisdn = msisdn
    },
    setRemainingAttempts (state, remainingAttempts) {
      state.remainingAttempts = remainingAttempts
    },
    setToken (state, token) {
      state.token = token
    },
    setMethod (state, method) {
      state.method = method
    },
    setMfaTokenValidUntil (state, mfaTokenValidUntil) {
      state.mfaTokenValidUntil = mfaTokenValidUntil
    }
  },
  actions: {
    async initiateMfa ({
      commit,
      state,
      rootGetters
    }) {
      const apolloClient = getProvider().defaultClient
      commit('setMfaLoading', true)
      try {
        const response = await apolloClient.mutate({
          mutation: gql`
              mutation mfaInitiate($mfaInitiateInput: MfaInitiateInput!) {
                mfaInitiate(mfaInitiateInput: $mfaInitiateInput) {
                activeMfa {
                  token
                  method
                  criticalEventType
                  validUntil
                  remainingAttempts
                  nextTanAt
                  locked
                  lockedUntil
                  cdReason
                  authenticated
                }
              }
            }
          `,
          variables: {
            mfaInitiateInput: {
              brand: rootGetters.brand,
              msisdn: state.msisdn,
              mfaMethod: state.method,
              mfaToken: state.token
            }
          }
        })
        commit('setRemainingAttempts', response.data.mfaInitiate.activeMfa.remainingAttempts)
        commit('setToken', response.data.mfaInitiate.activeMfa.token)
        commit('setMfaStatus', true)
      } catch (error) {
        console.error(error)
        throw error
      } finally {
        commit('setMfaLoading', false)
      }
    },
    async completeMfa ({
      commit,
      state,
      rootGetters
    }, tan) {
      const apolloClient = getProvider().defaultClient
      commit('setMfaLoading', true)
      try {
        const response = await apolloClient.mutate({
          mutation: gql`
          mutation mfaComplete($mfaCompleteInput: MfaCompleteInput!) {
            mfaComplete(mfaCompleteInput: $mfaCompleteInput) {
              success
            }
          }
        `,
          variables: {
            mfaCompleteInput: {
              brand: rootGetters.brand,
              msisdn: state.msisdn,
              mfaTan: tan,
              mfaToken: state.token
            }
          }
        })
        commit('setIsMfaCompleted', true)
        return response
      } catch (error) {
        // get error code and message
        if (splitError(error)[0].extensions.magma_code === 8931) {
          commit('setRemainingAttempts', 0)
        }
        throw error
      } finally {
        commit('setMfaLoading', false)
      }
    },
    async fetchMfaInfo ({
      commit,
      state,
      rootGetters
    }) {
      const apolloClient = getProvider().defaultClient
      commit('setMfaLoading', true)

      const query = gql`
      query MfaInfo($brand: String!, $msisdn: String!) {
        mfaInfo(mfa: {
          brand: $brand
          msisdn: $msisdn
        }) {
          active
          mfaMethods
          alternativeNumber
          hasAlternativeTanMethod
          activeMfas {
            token
            method
            criticalEventType
            validUntil
            remainingAttempts
            nextTanAt
            locked
            lockedUntil
            cdReason
            authenticated
          }
          locked
          loginPossible
        }
      }
    `
      try {
        const { data } = await apolloClient.query({
          query,
          variables: {
            brand: rootGetters.brand,
            msisdn: state.msisdn
          },
          fetchPolicy: 'no-cache'
        })
        const mfaObject = data && data.mfaInfo.activeMfas && data.mfaInfo.activeMfas.find(item => item.token === state.token && item.method === state.method)
        // If found, store the validUntil value
        if (mfaObject) {
          commit('setMfaTokenValidUntil', mfaObject.validUntil)
        } else {
          commit('setMfaTokenValidUntil', null)
        }
      } catch (error) {
        console.error(error)
        throw error
      } finally {
        commit('setMfaLoading', false)
      }
    }
  }
}

export default MfaModule
